import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import toast from 'react-hot-toast';
import { normalizeRole } from '../utils/roles';

interface User {
  id: string;
  nome: string;
  email: string;
  role: string;
  organizationId?: string | null;
  ativo: boolean;
  ultimoLogin?: string | null;
  criadoEm: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, senha: string) => Promise<boolean>;
  logout: () => void;
  checkAuth: () => Promise<boolean>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const API_BASE_URL = '/api';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('auth_token'));
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && !!token;

  // Interceptar requisições para adicionar token
  const apiRequest = useCallback(async (url: string, options: RequestInit = {}): Promise<Response> => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers,
    });
  }, [token]);

  const login = useCallback(async (email: string, senha: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, senha }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.message || 'Erro ao fazer login');
        return false;
      }

      // Padronizar extração de token e usuário - suporte para múltiplos formatos
      let newToken = null;
      let userData = null;

      // Formato atual da API: { success: true, token, user }
      if (data.success && data.token && data.user) {
        newToken = data.token;
        userData = data.user;
      }
      // Formato 1: { token, user }
      else if (data.token && data.user) {
        newToken = data.token;
        userData = data.user;
      }
      // Formato 2: { data: { token, user } }
      else if (data.data?.token && data.data?.user) {
        newToken = data.data.token;
        userData = data.data.user;
      }

      if (!newToken || !userData) {
        toast.error('Erro na resposta do servidor');
        return false;
      }

      const normalizedUser = {
        ...userData,
        role: normalizeRole(userData.role)
      };

      setToken(newToken);
      setUser(normalizedUser);
      localStorage.setItem('auth_token', newToken);

      toast.success('Login realizado com sucesso!');
      return true;
    } catch {
      toast.error('Erro de conexão. Tente novamente.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
    toast.success('Logout realizado com sucesso!');
  }, []);

  const checkAuth = useCallback(async (): Promise<boolean> => {
    if (!token) {
      setIsLoading(false);
      return false;
    }

    try {
      const response = await apiRequest('/auth/verify');

      if (!response.ok) {
        logout();
        return false;
      }

      const data = await response.json();
      const userData = data.user || data.data?.user;

      if (!userData) {
        logout();
        return false;
      }

      setUser({
        ...userData,
        role: normalizeRole(userData.role)
      });
      return true;
    } catch {
      logout();
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [apiRequest, logout, token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const refreshUser = useCallback(async (): Promise<void> => {
    if (!token) return;

    try {
      const response = await apiRequest('/auth/verify');

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const userData = data.user || data.data?.user;

      if (userData) {
        setUser({
          ...userData,
          role: normalizeRole(userData.role)
        });
      }
    } catch {
      // Silently fail
    }
  }, [apiRequest, token]);

  // Interceptar respostas 401 da API para fazer logout automático
  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      // Só faz logout se for uma resposta 401 de uma rota da API
      if (response.status === 401 && isAuthenticated && args[0]?.toString().includes('/api/')) {
        logout();
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [isAuthenticated, logout]);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuth,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
